import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Btn, Modal, Field, Input, Select, Badge } from "../components/kr";
import { DatePicker } from "../components/DatePicker";
import { useConfirm } from "../components/ConfirmDialog";
import { useAuth } from "../lib/auth";
import { useMultiSelect } from "../lib/multiSelect";
import MultiSelectBar from "../components/MultiSelectBar";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, X } from "@phosphor-icons/react";

const PRI_TONE = { low: "zinc", medium: "orange", high: "red", urgent: "red" };
const TONE_OPTIONS = ["zinc", "orange", "yellow", "green", "red"];

export default function Issues() {
  const [issues, setIssues] = useState([]);
  const [columns, setColumns] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(null);
  const [colOpen, setColOpen] = useState(null);
  const [drag, setDrag] = useState(null);
  const { confirm } = useConfirm();
  const { user } = useAuth();
  const ms = useMultiSelect();

  const load = async () => {
    const [a, b, u] = await Promise.all([
      api.get("/issues"),
      api.get("/issue-columns"),
      api.get("/users").catch(() => ({ data: [] })),
    ]);
    setIssues(a.data); setColumns(b.data); setUsers(u.data || []);
  };
  useEffect(() => { load(); }, []);

  const move = async (issue, newStatus) => {
    if (issue.status === newStatus) return;
    setIssues(issues.map(i => i.id === issue.id ? { ...i, status: newStatus } : i));
    try { await api.put(`/issues/${issue.id}`, { status: newStatus }); }
    catch { load(); toast.error("Failed to move"); }
  };

  const del = async (id) => {
    const issue = issues.find(i => i.id === id);
    const ok = await confirm({
      title: "Delete issue?",
      message: issue ? `${issue.issue_no} · ${issue.title}` : "This issue will be permanently removed.",
      tone: "danger",
    });
    if (!ok) return;
    await api.delete(`/issues/${id}`);
    load();
  };

  const delCol = async (id) => {
    const col = columns.find(c => c.id === id);
    const ok = await confirm({
      title: "Delete column?",
      message: `"${col?.label || "This column"}" will be removed. Any issues in it will fall back to Backlog.`,
      tone: "warning",
    });
    if (!ok) return;
    await api.delete(`/issue-columns/${id}`);
    load();
  };

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        label="ROADMAP"
        title="Issues Board"
        subtitle="GitLab-style board for bugs, improvements & ideas. Drag cards across columns."
        action={
          <div className="flex gap-2">
            <Btn variant="secondary" onClick={() => setColOpen("new")} data-testid="add-column"><Plus size={14} /> Column</Btn>
            <Btn onClick={() => setOpen("new")} data-testid="add-issue"><Plus size={14} /> New Issue</Btn>
          </div>
        }
      />

      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.max(columns.length, 1)}, minmax(220px, 1fr))` }}>
        {columns.map(col => {
          const items = issues.filter(i => i.status === col.key);
          return (
            <div
              key={col.id}
              data-testid={`column-${col.key}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => drag && move(drag, col.key)}
              className="bg-white border border-zinc-200 min-h-[60vh] flex flex-col"
            >
              <div className="px-3 py-2 border-b border-zinc-200 flex items-center justify-between bg-zinc-50">
                <div className="flex items-center gap-2">
                  <Badge tone={col.tone || "zinc"}>{col.label}</Badge>
                  <span className="text-xs font-mono text-zinc-500">{items.length}</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setColOpen(col)} className="text-zinc-400 hover:text-orange-600" data-testid={`edit-col-${col.key}`}>
                    <PencilSimple size={12} weight="bold" />
                  </button>
                  <button onClick={() => delCol(col.id)} className="text-zinc-400 hover:text-red-600">
                    <X size={12} weight="bold" />
                  </button>
                </div>
              </div>
              <div className="p-2 space-y-2 flex-1 overflow-y-auto">
                {items.map(i => (
                  <div
                    key={i.id}
                    draggable
                    onDragStart={() => setDrag(i)}
                    onDragEnd={() => setDrag(null)}
                    onClick={() => setOpen(i)}
                    data-testid={`issue-${i.issue_no}`}
                    className="border border-zinc-200 bg-white p-3 cursor-grab hover:border-orange-500 active:cursor-grabbing"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      {user?.role === "admin" && (
                        <input
                          type="checkbox"
                          checked={ms.isSelected(i.id)}
                          onClick={(e) => { e.stopPropagation(); ms.toggle(i.id); }}
                          onChange={() => {}}
                          className="w-3.5 h-3.5 accent-orange-600 cursor-pointer"
                          data-testid={`issue-check-${i.issue_no}`}
                        />
                      )}
                      <span className="font-mono text-[10px] text-zinc-500">{i.issue_no}</span>
                      <Badge tone={PRI_TONE[i.priority]}>{i.priority}</Badge>
                    </div>
                    <div className="font-bold text-sm leading-tight">{i.title}</div>
                    {(i.assignees && i.assignees.length > 0) ? (
                      <div className="text-[10px] text-zinc-500 mt-2 font-mono flex flex-wrap gap-1" data-testid={`assignees-${i.issue_no.replace('#','')}`}>
                        {i.assignees.map(a => (
                          <span key={a} className="bg-zinc-100 border border-zinc-200 px-1.5 py-0.5">@{a.split("@")[0]}</span>
                        ))}
                      </div>
                    ) : (i.assignee && (
                      <div className="text-[10px] text-zinc-500 mt-2 font-mono">@{i.assignee}</div>
                    ))}
                    {i.labels?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {i.labels.map(l => <span key={l} className="text-[9px] bg-orange-50 text-orange-700 border border-orange-200 px-1.5 py-0.5 font-bold uppercase">{l}</span>)}
                      </div>
                    )}
                  </div>
                ))}
                {items.length === 0 && (
                  <div className="text-center text-[10px] font-mono text-zinc-400 py-6 border border-dashed border-zinc-200">
                    Drop here
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {open && <IssueModal issue={open === "new" ? null : open} columns={columns} users={users} onClose={() => setOpen(null)}
        onSaved={() => { setOpen(null); load(); }}
        onDelete={async (id) => { await del(id); setOpen(null); }} />}
      {colOpen && <ColumnModal col={colOpen === "new" ? null : colOpen} onClose={() => setColOpen(null)}
        onSaved={() => { setColOpen(null); load(); }} />}
      <MultiSelectBar
        count={ms.selected.length}
        ids={ms.selected}
        resource="issues"
        label="issues"
        onCleared={() => { ms.clear(); load(); }}
      />
    </div>
  );
}

function IssueModal({ issue, columns, users, onClose, onSaved, onDelete }) {
  const isNew = !issue;
  const { user: me } = useAuth();
  const [tab, setTab] = useState("details");
  const [fresh, setFresh] = useState(issue || null);
  const [f, setF] = useState(issue ? {
    ...issue,
    labels: (issue.labels || []).join(", "),
    assignees: issue.assignees || (issue.assignee ? [issue.assignee] : []),
  } : {
    title: "", description: "", status: columns[0]?.key || "backlog", priority: "medium",
    assignees: [], labels: "", due_date: "",
  });
  const [commentBody, setCommentBody] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);
  const upd = (k, v) => setF({ ...f, [k]: v });

  const refresh = React.useCallback(async () => {
    if (!issue) return;
    try {
      const r = await api.get(`/issues/${issue.id}`);
      setFresh(r.data);
    } catch (err) {
      // Non-fatal: the issue detail refresh piggybacks on the parent list, so
      // if this individual GET fails we log for observability but avoid a
      // scary toast — the modal already shows stale-but-usable data.
      // eslint-disable-next-line no-console
      console.warn("issue refresh failed", err);
    }
  }, [issue]);
  React.useEffect(() => { refresh(); }, [refresh]);

  const toggleAssignee = (email) => {
    const cur = new Set(f.assignees || []);
    if (cur.has(email)) cur.delete(email);
    else cur.add(email);
    setF({ ...f, assignees: Array.from(cur) });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...f,
        assignees: f.assignees || [],
        assignee: (f.assignees || [])[0] || "",  // keep legacy field in sync
        labels: f.labels ? f.labels.split(",").map(s => s.trim()).filter(Boolean) : [],
      };
      if (isNew) await api.post("/issues", body);
      else await api.put(`/issues/${issue.id}`, body);
      toast.success(isNew ? "Created" : "Updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!commentBody.trim()) return;
    setCommentBusy(true);
    try {
      await api.post(`/issues/${issue.id}/comments`, { body: commentBody.trim() });
      setCommentBody("");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to post"); }
    finally { setCommentBusy(false); }
  };

  const removeComment = async (eid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/issues/${issue.id}/comments/${eid}`);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const events = (fresh?.events || []).slice().sort((a, b) => (a.at || "").localeCompare(b.at || ""));

  return (
    <Modal open onClose={onClose} title={isNew ? "New Issue" : `${issue.issue_no} · ${issue.title || ""}`} size="lg">
      {!isNew && (
        <div className="flex gap-1 border-b border-zinc-200 -mx-5 px-5 -mt-1 mb-4">
          <button type="button" onClick={() => setTab("details")} data-testid="tab-details"
                  className={`px-3 py-2 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px ${tab === "details" ? "border-orange-600 text-orange-700" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
            Details
          </button>
          <button type="button" onClick={() => setTab("activity")} data-testid="tab-activity"
                  className={`px-3 py-2 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px ${tab === "activity" ? "border-orange-600 text-orange-700" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
            Activity <span className="ml-1 text-[10px] font-bold text-zinc-400">{events.length}</span>
          </button>
        </div>
      )}

      {(isNew || tab === "details") && (
        <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="issue-form">
          <Field label="Title" full><Input value={f.title} onChange={e => upd("title", e.target.value)} required data-testid="issue-title" /></Field>
          <Field label="Status">
            <Select value={f.status} onChange={e => upd("status", e.target.value)} data-testid="issue-status">
              {columns.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={f.priority} onChange={e => upd("priority", e.target.value)} data-testid="issue-priority">
              {["low", "medium", "high", "urgent"].map(p => <option key={p}>{p}</option>)}
            </Select>
          </Field>
          <Field label="Assignees (multi-select)" full>
            <div className="border border-zinc-300 max-h-40 overflow-y-auto bg-white dark:bg-zinc-900" data-testid="assignees-picker">
              {(users || []).length === 0 ? (
                <div className="p-2 text-xs text-zinc-500 font-mono">No users found</div>
              ) : (
                users.map(u => (
                  <label key={u.id || u.email}
                         className="flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 dark:hover:bg-zinc-800 cursor-pointer border-b border-zinc-100 last:border-0">
                    <input
                      type="checkbox"
                      checked={(f.assignees || []).includes(u.email)}
                      onChange={() => toggleAssignee(u.email)}
                      className="accent-orange-600"
                      data-testid={`assignee-check-${u.email}`}
                    />
                    <span className="font-mono text-sm">{u.name || u.email}</span>
                    <span className="ml-auto text-[10px] font-mono text-zinc-400">{u.role}</span>
                  </label>
                ))
              )}
            </div>
            {(f.assignees || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {f.assignees.map(a => (
                  <span key={a} className="text-[10px] bg-orange-50 text-orange-700 border border-orange-200 px-1.5 py-0.5 font-bold uppercase">
                    @{a.split("@")[0]}
                  </span>
                ))}
              </div>
            )}
          </Field>
          <Field label="Due Date"><DatePicker value={f.due_date || ""} onChange={e => upd("due_date", e.target.value)} /></Field>
          <Field label="Labels (comma separated)"><Input value={f.labels} onChange={e => upd("labels", e.target.value)} placeholder="bug, frontend" /></Field>
          <Field label="Description" full>
            <textarea value={f.description} onChange={e => upd("description", e.target.value)} rows={5}
              className="w-full px-3 py-2 border border-zinc-300 bg-white text-sm focus:outline-none focus:border-orange-600" />
          </Field>
          <div className="col-span-2 flex justify-between">
            {!isNew && <Btn type="button" variant="danger" onClick={() => onDelete(issue.id)}><Trash size={14} /> Delete</Btn>}
            <div className="ml-auto flex gap-2">
              <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
              <Btn type="submit" data-testid="issue-submit">{isNew ? "Create" : "Save"}</Btn>
            </div>
          </div>
        </form>
      )}

      {!isNew && tab === "activity" && (
        <div className="space-y-4" data-testid="activity-tab">
          <div className="max-h-96 overflow-y-auto pr-2" data-testid="activity-timeline">
            {events.length === 0 ? (
              <div className="text-sm text-zinc-500 font-mono py-4">No activity yet.</div>
            ) : (
              <ol className="relative border-l-2 border-zinc-200 ml-2 space-y-4">
                {events.map(ev => (
                  <TimelineEvent key={ev.id} ev={ev} me={me}
                    onDeleteComment={() => removeComment(ev.id)} />
                ))}
              </ol>
            )}
          </div>
          <form onSubmit={postComment} className="flex gap-2" data-testid="comment-form">
            <textarea
              value={commentBody}
              onChange={e => setCommentBody(e.target.value)}
              rows={2}
              placeholder="Write a comment…"
              className="flex-1 px-3 py-2 border border-zinc-300 bg-white text-sm focus:outline-none focus:border-orange-600"
              data-testid="comment-input"
            />
            <Btn type="submit" disabled={commentBusy || !commentBody.trim()} data-testid="comment-submit">
              {commentBusy ? "Posting…" : "Comment"}
            </Btn>
          </form>
        </div>
      )}
    </Modal>
  );
}

function TimelineEvent({ ev, me, onDeleteComment }) {
  const when = ev.at ? new Date(ev.at).toLocaleString() : "";
  const author = ev.by_name || ev.by || "someone";
  const canDelete = ev.type === "comment" && (me?.email === ev.by || me?.role === "admin");
  const dotColor = {
    created: "bg-green-500",
    status_changed: "bg-blue-500",
    assignees_changed: "bg-purple-500",
    edited: "bg-zinc-400",
    comment: "bg-orange-500",
  }[ev.type] || "bg-zinc-300";
  return (
    <li className="ml-4" data-testid={`ev-${ev.id}`}>
      <span className={`absolute w-3 h-3 rounded-full -left-[7px] mt-1.5 border-2 border-white ${dotColor}`} />
      {ev.type === "comment" ? (
        <div className="bg-zinc-50 border border-zinc-200 p-3">
          <div className="flex items-baseline justify-between mb-1">
            <div className="text-xs font-bold">{author} <span className="text-zinc-500 font-normal">commented</span></div>
            <div className="text-[10px] font-mono text-zinc-500 flex items-center gap-2">
              <span>{when}</span>
              {canDelete && (
                <button type="button" onClick={onDeleteComment} className="text-red-600 hover:text-red-800"
                        title="Delete comment" data-testid={`del-comment-${ev.id}`}>
                  <Trash size={12} weight="bold" />
                </button>
              )}
            </div>
          </div>
          <div className="text-sm whitespace-pre-wrap">{ev.message}</div>
        </div>
      ) : (
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-xs">
            <span className="font-bold">{author}</span>{" "}
            <span className="text-zinc-600">{ev.message}</span>
          </div>
          <div className="text-[10px] font-mono text-zinc-500 whitespace-nowrap">{when}</div>
        </div>
      )}
    </li>
  );
}

function ColumnModal({ col, onClose, onSaved }) {
  const isNew = !col;
  const [f, setF] = useState(col || { key: "", label: "", order: 99, tone: "zinc" });
  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, order: parseInt(f.order || 0) };
      if (isNew) {
        body.key = (body.key || body.label).toLowerCase().replace(/[^a-z0-9_]/g, "_");
        await api.post("/issue-columns", body);
      } else {
        await api.put(`/issue-columns/${col.id}`, body);
      }
      toast.success(isNew ? "Column added" : "Updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "New Column" : `Edit · ${col.label}`}>
      <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="column-form">
        <Field label="Label" full><Input value={f.label} onChange={e => upd("label", e.target.value)} required data-testid="col-label" placeholder="e.g. Blocked" /></Field>
        <Field label="Key" full><Input value={f.key} onChange={e => upd("key", e.target.value)} disabled={!isNew} placeholder="auto from label" /></Field>
        <Field label="Order"><Input type="number" value={f.order} onChange={e => upd("order", e.target.value)} /></Field>
        <Field label="Color">
          <Select value={f.tone} onChange={e => upd("tone", e.target.value)}>
            {TONE_OPTIONS.map(t => <option key={t}>{t}</option>)}
          </Select>
        </Field>
        <div className="col-span-2 flex justify-end gap-2">
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" data-testid="col-submit">{isNew ? "Add" : "Save"}</Btn>
        </div>
      </form>
    </Modal>
  );
}


